import { createInitialWizardState } from '../config/wizardSteps';
import { emptyDeploymentStatusDates, mapDeploymentStatusApprovalsFromApi, toDatetimeLocalValue } from '../shared/DeploymentLifecycleFields';
import { emptyBusinessProcessRow } from '../shared/BusinessProcessUseCaseDialog';
import { emptySystemDocumentRow } from '../shared/SystemDocumentDialog';
import { emptyMonitoringReviewRow } from '../shared/MonitoringReviewDialog';
import { emptyIncidentRow } from '../shared/IncidentDialog';
import { emptyChangeManagementRow } from '../shared/ChangeManagementDialog';
import { emptyIntegrationRow } from '../shared/IntegrationDialog';

function resolveRegulationIds(detail, regulationsLookup = []) {
  const types = detail?.regulationTypes;
  if (!types?.length) return [];
  const names = new Set(types);
  return regulationsLookup
    .filter((r) => names.has(r.regulationName))
    .map((r) => r.id);
}

function hasStoredDataModel(detail) {
  const di = detail?.dataInformation;
  const sources = detail?.dataSources ?? [];
  return Boolean(
    sources.length
      || di?.personalData
      || (di?.sensitiveDataCategories?.length ?? 0) > 0
      || di?.modelType
      || di?.trainingDataOrigin
  );
}

/** Map full SoftwareResponseDto → wizard state. */
export function detailToWizardState(detail, regulationsLookup = []) {
  if (!detail) {
    return createInitialWizardState();
  }

  const review = detail.reviews?.[0];
  const approval = detail.approvals?.[0];
  const usesAi = detail.riskAssessment?.usesArtificialIntelligence != null
    ? Boolean(detail.riskAssessment.usesArtificialIntelligence)
    : hasStoredDataModel(detail);

  return {
    softwareId: detail.id ?? null,
    meta: {
      dataInformationId: detail.dataInformation?.id ?? null,
      riskAssessmentId: detail.riskAssessment?.id ?? null,
      businessCaseId: detail.businessCase?.id ?? null,
      reviewId: review?.id ?? null,
      incidentId: detail.incidents?.[0]?.id ?? null,
      approvalId: approval?.id ?? null,
      documentIds: (detail.documents ?? []).map((d) => d.id),
      dataSourceIds: (detail.dataSources ?? []).map((d) => d.id),
    },
    softwareIdentification: {
      softwareName: detail.softwareName ?? '',
      internalReference: detail.internalReference ?? '',
      externalOrganisationReference: detail.externalOrganisationReference ?? '',
      contractStartDate: detail.contractStartDate ?? '',
      contractEndDate: detail.contractEndDate ?? '',
      contractDuration: detail.contractDuration ?? '',
      contractDurationUnit: detail.contractDurationUnit ?? 'DAYS',
      licenseStartDate: detail.licenseStartDate ?? '',
      licenseEndDate: detail.licenseEndDate ?? '',
      licenseDuration: detail.licenseDuration ?? '',
      licenseDurationUnit: detail.licenseDurationUnit ?? 'DAYS',
      deploymentEnvironment: detail.deploymentEnvironment ?? '',
      systemIpAddress: detail.systemIpAddress ?? '',
      systemUrl: detail.systemUrl ?? '',
      hostingServerName: detail.hostingServerName ?? '',
      deploymentStatus: detail.deploymentStatus ?? '',
      deploymentStatusDates: {
        ...emptyDeploymentStatusDates(),
        planned: detail.deploymentStatusDates?.planned ?? '',
        development: detail.deploymentStatusDates?.development ?? '',
        testing: detail.deploymentStatusDates?.testing ?? '',
        pilot: detail.deploymentStatusDates?.pilot ?? '',
        production: detail.deploymentStatusDates?.production ?? detail.firstDeployedDate ?? '',
        retired: detail.deploymentStatusDates?.retired ?? '',
        suspended: detail.deploymentStatusDates?.suspended ?? '',
      },
      firstDeployedDate: detail.firstDeployedDate ?? detail.deploymentStatusDates?.production ?? '',
      deploymentStatusApprovals: mapDeploymentStatusApprovalsFromApi(detail.deploymentStatuses),
      deploymentStatusesList: detail.deploymentStatuses ?? [],
      businessUnitIds: (detail.businessUnits ?? []).map((b) => b.id),
      systemOwnerIds: (detail.systemOwners ?? []).map((o) => o.id),
      vendorIds: (detail.vendors ?? []).map((v) => v.id),
    },
    useCase: {
      businessCase: detail.businessCase
        ? {
            key: `bc-${detail.businessCase.id}`,
            id: detail.businessCase.id ?? null,
            caseDate: detail.businessCase.caseDate ?? '',
            submittedBy: detail.businessCase.submittedBy ?? '',
            titleRole: detail.businessCase.titleRole ?? '',
            projectOverview: detail.businessCase.projectOverview ?? '',
            backgroundCurrentSituation: detail.businessCase.backgroundCurrentSituation ?? '',
            proposedSolution: detail.businessCase.proposedSolution ?? '',
            technicalRequirementsIntegration:
              detail.businessCase.technicalRequirementsIntegration ?? '',
            securityComplianceConsiderations:
              detail.businessCase.securityComplianceConsiderations ?? '',
            implementationPlan: detail.businessCase.implementationPlan ?? '',
            costEstimate: detail.businessCase.costEstimate ?? '',
            benefitsImpactsOnUsers: detail.businessCase.benefitsImpactsOnUsers ?? '',
            performanceMetrics: detail.businessCase.performanceMetrics ?? '',
            risksAndLimitations: detail.businessCase.risksAndLimitations ?? '',
          }
        : null,
      businessProcesses: (detail.businessProcesses?.length
        ? detail.businessProcesses
        : detail.businessProcessSupported
          ? [{
              businessProcessId: '',
              businessProcessName: detail.businessProcessSupported,
              decisionType: detail.decisionType ?? '',
              humanOversight: Boolean(detail.humanOversight),
              humanOversightExplanation: detail.humanOversightExplanation ?? '',
              businessOwner: '',
            }]
          : []
      ).map((link) => ({
        ...emptyBusinessProcessRow(),
        businessProcessId: link.businessProcessId ?? '',
        decisionType: link.decisionType ?? '',
        humanOversight: Boolean(link.humanOversight),
        humanOversightExplanation: link.humanOversightExplanation ?? '',
        businessOwner: link.businessOwner ?? '',
      })),
    },
    dataModel: {
      dataSourcesUsed: (detail.dataSources ?? []).map((ds) => ds.sourceName).filter(Boolean).join('\n'),
      personalData: Boolean(detail.dataInformation?.personalData),
      sensitiveDataCategories: detail.dataInformation?.sensitiveDataCategories ?? [],
      modelType: detail.dataInformation?.modelType ?? '',
      trainingDataOrigin: detail.dataInformation?.trainingDataOrigin ?? '',
    },
    riskImpact: {
      usesArtificialIntelligence: usesAi,
      regulationIds: resolveRegulationIds(detail, regulationsLookup),
      knownBias: Boolean(detail.riskAssessment?.knownBias),
      biasDescription: detail.riskAssessment?.biasDescription ?? '',
      risks: (detail.softwareRisks ?? []).map((item) => ({
        key: `risk-${item.id ?? Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
        id: item.id ?? null,
        riskRegisterId: item.riskRegisterId ?? null,
        riskCategory: item.riskCategory ?? '',
        resourcesImpacted: item.resourcesImpacted ?? '',
        riskName: item.riskName ?? '',
        riskPriority: item.riskPriority ?? '',
        riskDescription: item.riskDescription ?? '',
        riskOwner: item.riskOwner ?? '',
        impact: item.impact ?? '',
        causes: item.causes ?? '',
        controlsAndMitigation: item.controlsAndMitigation ?? '',
        status: item.status ?? 'OPEN',
      })),
    },
    complianceGovernance: {
      documents: (detail.documents ?? []).map((doc) => ({
        ...emptySystemDocumentRow(),
        id: doc.id ?? null,
        documentName: doc.documentName ?? '',
        documentType: doc.documentType ?? '',
        deploymentStatus: doc.deploymentStatus ?? '',
        fileName: doc.fileName ?? '',
        contentType: doc.contentType ?? '',
        location: doc.location ?? '',
      })),
    },
    monitoringReview: {
      entries: (detail.monitoringReviews ?? []).map((item) => ({
        ...emptyMonitoringReviewRow(),
        // id = catalog master (approval / view pages); linkId = SoftwareMonitoringAndReview join
        id: item.monitoringReviewId ?? item.id ?? null,
        linkId: item.linkId ?? null,
        monitoringReviewId: item.monitoringReviewId ?? item.id ?? null,
        monitoringType: item.monitoringType ?? 'PERFORMANCE_MONITORING',
        reviewAreaControl: item.reviewAreaControl ?? '',
        standardControlReference: item.standardControlReference ?? '',
        reviewFrequency: item.reviewFrequency ?? '',
        observationFindings: item.observationFindings ?? '',
        riskRating: item.riskRating ?? '',
        correctiveActionRequired: item.correctiveActionRequired ?? '',
        actionOwnerEmail: item.actionOwnerEmail ?? '',
        status: item.status ?? 'OPEN',
        nextReviewDate: item.nextReviewDate ?? '',
        evidenceFileName: item.evidenceFileName ?? '',
        evidenceContentType: item.evidenceContentType ?? '',
        evidenceLocation: item.evidenceLocation ?? '',
        reviewer: item.reviewer ?? '',
        approvals: (item.approvals ?? []).map((a) => ({
          id: a.id ?? null,
          monitoringReviewId: a.monitoringReviewId ?? item.monitoringReviewId ?? item.id ?? null,
          approverName: a.approverName ?? '',
          approverRole: a.approverRole ?? '',
          approverCommittee: a.approverCommittee ?? '',
          approvalDate: a.approvalDate ?? '',
          approvalStatus: a.approvalStatus ?? '',
          approvalComments: a.approvalComments ?? '',
          approvalLevel: a.approvalLevel ?? '',
          signatureConfirmation: a.signatureConfirmation ?? '',
          jobPosition: a.jobPosition ?? a.job_position ?? '',
        })),
      })),
    },
    incidentEscalation: {
      entries: (detail.incidents ?? []).map((item) => ({
        ...emptyIncidentRow(),
        id: item.id ?? null,
        incidentTicket: item.incidentTicket ?? '',
        incidentDate: item.incidentDate ?? '',
        reportedAt: item.reportedAt ?? '',
        solvedApprovedAt: item.solvedApprovedAt ?? '',
        incidentDetails: item.incidentDetails ?? '',
        actionTaken: item.actionTaken ?? '',
        rootCause: item.rootCause ?? '',
        reportedBy: item.reportedBy ?? '',
        incidentReportingStaff: item.incidentReportingStaff ?? '',
        riskRegisterIds: Array.isArray(item.riskRegisterIds)
          ? item.riskRegisterIds
          : item.riskRegisterId != null
            ? [item.riskRegisterId]
            : [],
        risks: Array.isArray(item.risks)
          ? item.risks
          : item.riskRegisterId != null
            ? [{
                riskRegisterId: item.riskRegisterId,
                riskName: item.riskName ?? '',
                riskPriority: item.riskPriority ?? item.severityLevel ?? '',
              }]
            : [],
        riskPriority: item.riskPriority ?? item.severityLevel ?? '',
        severityLevel: item.severityLevel ?? item.riskPriority ?? '',
        resolutionStatus: item.resolutionStatus ?? 'OPEN',
        incidentResponseOwner: item.incidentResponseOwner ?? '',
        knownIncidents: Boolean(item.knownIncidents ?? true),
        escalationPathDefined: Boolean(item.escalationPathDefined),
        approvals: (item.approvals ?? []).map((a) => ({
          id: a.id ?? null,
          incidentId: a.incidentId ?? item.id ?? null,
          approverName: a.approverName ?? '',
          approverRole: a.approverRole ?? '',
          approverCommittee: a.approverCommittee ?? '',
          approvalDate: a.approvalDate ?? '',
          approvalStatus: a.approvalStatus ?? '',
          approvalComments: a.approvalComments ?? '',
          approvalLevel: a.approvalLevel ?? '',
          signatureConfirmation: a.signatureConfirmation ?? '',
          jobPosition: a.jobPosition ?? a.job_position ?? '',
        })),
      })),
    },
    changeManagement: {
      entries: (detail.changeManagements ?? []).map((item) => ({
        ...emptyChangeManagementRow(),
        id: item.id ?? null,
        title: item.title ?? '',
        description: item.description ?? '',
        changeType: item.changeType ?? '',
        priority: item.priority ?? '',
        riskLevel: item.riskLevel ?? '',
        reasonForChange: item.reasonForChange ?? '',
        requestedBy: item.requestedBy ?? '',
        requestedDate: toDatetimeLocalValue(item.requestedDate),
        currentVersion: item.currentVersion ?? '',
        targetVersion: item.targetVersion ?? '',
        implementationPlan: item.implementationPlan ?? '',
        rollbackPlan: item.rollbackPlan ?? '',
        testPlan: item.testPlan ?? '',
        testResult: item.testResult ?? '',
        scheduledStart: toDatetimeLocalValue(item.scheduledStart),
        scheduledEnd: toDatetimeLocalValue(item.scheduledEnd),
        actualStart: toDatetimeLocalValue(item.actualStart),
        actualEnd: toDatetimeLocalValue(item.actualEnd),
        downtimeRequired: Boolean(item.downtimeRequired),
        downtimeDuration: item.downtimeDuration ?? '',
        implementationStatus: item.implementationStatus ?? 'PLANNED',
        implementedBy: item.implementedBy ?? '',
        createdAt: toDatetimeLocalValue(item.createdAt),
        updatedAt: toDatetimeLocalValue(item.updatedAt),
        approvals: (item.approvals ?? []).map((a) => ({
          id: a.id ?? null,
          changeManagementId: a.changeManagementId ?? item.id ?? null,
          approverName: a.approverName ?? '',
          approverRole: a.approverRole ?? '',
          approverCommittee: a.approverCommittee ?? '',
          approvalDate: a.approvalDate ?? '',
          approvalStatus: a.approvalStatus ?? '',
          approvalComments: a.approvalComments ?? '',
          approvalLevel: a.approvalLevel ?? '',
          signatureConfirmation: a.signatureConfirmation ?? '',
          jobPosition: a.jobPosition ?? a.job_position ?? '',
        })),
      })),
    },
    integration: {
      integratedWithOtherSystems:
        detail.integratedWithOtherSystems == null
          ? null
          : Boolean(detail.integratedWithOtherSystems),
      entries: (detail.integrations ?? []).map((item) => ({
        ...emptyIntegrationRow(),
        id: item.id ?? null,
        sourceSystem: item.sourceSystem ?? '',
        targetSystem: item.targetSystem ?? '',
        integrationType: item.integrationType ?? '',
        integrationPurpose: item.integrationPurpose ?? '',
        dataFlowDirection: item.dataFlowDirection ?? '',
        dataExchanged: item.dataExchanged ?? '',
        integrationMethod: item.integrationMethod ?? '',
        authenticationMethod: item.authenticationMethod ?? '',
        integrationOwner: item.integrationOwner ?? '',
        securityMeasures: item.securityMeasures ?? '',
        integrationStatus: item.integrationStatus ?? '',
      })),
    },
    approvalSignOff: {
      reviewedBy: approval?.approverName ?? review?.reviewedBy ?? '',
      reviewerRoleCommittee: approval?.approverCommittee ?? review?.reviewerRoleCommittee ?? '',
      approvalDate: approval?.approvalDate ?? '',
      approvalStatus: approval?.approvalStatus ?? '',
    },
  };
}
